import type { AdminRole } from "@repo/api-dtos"
import { useLocation } from "@tanstack/react-router"
import * as React from "react"
import type {
	LiveBadgeKind,
	NavGroup,
	NavGroupConfig,
	NavPage,
	NavTab,
} from "@/config/navigation"
import { getNavGroups, NAV_GROUPS } from "@/config/navigation"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"

type HasAnyRole = (roles: AdminRole[]) => boolean

/**
 * Keeps the tabs the admin may open (any of their roles, or no roles
 * required), then the pages with at least one such tab, then the groups with
 * at least one such page.
 */
export function filterNavGroups(
	groups: NavGroupConfig[],
	hasAnyRole: HasAnyRole,
): NavGroupConfig[] {
	const visibleGroups: NavGroupConfig[] = []

	for (const group of groups) {
		const pages: NavPage[] = []

		for (const page of group.pages) {
			const tabs = page.tabs.filter(
				(tab) => !tab.requiredRoles || hasAnyRole(tab.requiredRoles),
			)

			if (tabs.length > 0) {
				pages.push({ ...page, tabs })
			}
		}

		if (pages.length > 0) {
			visibleGroups.push({ ...group, pages })
		}
	}

	return visibleGroups
}

/** The live badge of the first visible tab that carries one. */
export function pageLiveBadge(page: NavPage): LiveBadgeKind | undefined {
	return page.tabs.find((tab) => tab.liveBadge)?.liveBadge
}

/** Where a page leads: its first tab, so a filtered page leads to its first visible tab. */
export function pageHref(page: NavPage): string {
	return page.tabs[0]?.href ?? "/"
}

function matchesPath(path: string, pathname: string): boolean {
	return path === "/"
		? pathname === "/"
		: pathname === path || pathname.startsWith(`${path}/`)
}

function normalisePathname(pathname: string): string {
	return pathname.length > 1 && pathname.endsWith("/")
		? pathname.slice(0, -1)
		: pathname
}

export interface NavLocation {
	group: NavGroupConfig
	page: NavPage
	tab: NavTab
}

/**
 * The tab with the longest path (its href or one of its `activePaths`) that
 * prefixes the current path, so a detail page such as `/orders/abc123` still
 * belongs to Orders.
 */
export function resolveNavLocation(
	groups: NavGroupConfig[],
	pathname: string,
): NavLocation | undefined {
	const path = normalisePathname(pathname)
	let best: NavLocation | undefined
	let bestLength = 0

	for (const group of groups) {
		for (const page of group.pages) {
			for (const tab of page.tabs) {
				for (const candidate of [tab.href, ...(tab.activePaths ?? [])]) {
					if (matchesPath(candidate, path) && candidate.length > bestLength) {
						best = { group, page, tab }
						bestLength = candidate.length
					}
				}
			}
		}
	}

	return best
}

/**
 * The page and tab whose href or one of its `activePaths` equals the current
 * path exactly, when that page has more than one tab. Detail pages below a
 * tab never match.
 */
export function resolveTabBar(
	groups: NavGroupConfig[],
	pathname: string,
): NavLocation | undefined {
	const path = normalisePathname(pathname)

	for (const group of groups) {
		for (const page of group.pages) {
			if (page.tabs.length < 2) continue

			for (const tab of page.tabs) {
				if (tab.href === path || tab.activePaths?.includes(path)) {
					return { group, page, tab }
				}
			}
		}
	}

	return undefined
}

/** The navigation the current admin may see, and where the current route sits in it. */
export function useNavModel() {
	const { hasAnyRole } = useAdminRolesContext()
	const pathname = useLocation({ select: (location) => location.pathname })
	const groups = React.useMemo(
		() => filterNavGroups(getNavGroups(), hasAnyRole),
		[hasAnyRole],
	)
	const location = React.useMemo(
		() => resolveNavLocation(groups, pathname),
		[groups, pathname],
	)

	return { groups, location, pathname }
}

const PANEL_STORAGE_KEY = "admin-nav-page-panel-open"

function loadPanelOpen(): boolean {
	try {
		return localStorage.getItem(PANEL_STORAGE_KEY) !== "false"
	} catch {
		return true
	}
}

/**
 * Whether the group page panel next to the rail is open, remembered in
 * localStorage. It drives the sidebar provider, so the header toggle and ⌘B
 * open and close the panel.
 */
export function usePagePanelOpen(): [boolean, (open: boolean) => void] {
	const [open, setOpenState] = React.useState(loadPanelOpen)

	const setOpen = React.useCallback((next: boolean) => {
		setOpenState(next)

		try {
			localStorage.setItem(PANEL_STORAGE_KEY, String(next))
		} catch {
			// Storage is unavailable: the panel state lasts until the next reload.
		}
	}, [])

	return [open, setOpen]
}

const PANEL_GROUP_STORAGE_KEY = "admin-nav-page-panel-group"

function loadPanelGroup(): NavGroup | undefined {
	try {
		const stored = localStorage.getItem(PANEL_GROUP_STORAGE_KEY)
		return Object.values(NAV_GROUPS).find((group) => group === stored)
	} catch {
		return undefined
	}
}

/**
 * The group whose pages the panel lists, remembered in localStorage so a
 * reload on a single-page group keeps the last group shown. Starts from
 * `initial` (the current route's group) when given.
 */
export function usePanelGroupKey(
	initial: NavGroup | undefined,
): [NavGroup | undefined, (key: NavGroup) => void] {
	const [key, setKeyState] = React.useState(() => initial ?? loadPanelGroup())

	const setKey = React.useCallback((next: NavGroup) => {
		setKeyState(next)

		try {
			localStorage.setItem(PANEL_GROUP_STORAGE_KEY, next)
		} catch {
			// Storage is unavailable: the group lasts until the next reload.
		}
	}, [])

	return [key, setKey]
}
